import { createContext, useContext, useMemo } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createLineup,
  getCurrentLineup,
  getNextWeekLineup,
  getRecruitmentPrice,
  tradeLineup,
  ApiError,
} from "../api";
import {
  CreateLineupBody,
  CreateLineupResponse,
  CurrentLineup,
  EnrichedCurrentLineup,
  EnrichedNextWeekLineup,
  NextWeekLineup,
  RecruitmentPriceResponse,
  TradeLineupBody,
  TradeLineupResponse,
} from "../api/types";
import { playerKeys } from "./player";
import { useGetArtistPrices, useGetArtistsPoints } from "./artists";

const lineupKeys = {
  all: ["lineup"] as const,
  current: (
    playerId: string,
    draftId?: string,
    lineupIndex?: number,
    date?: string,
  ) =>
    [
      ...lineupKeys.all,
      "current",
      playerId,
      draftId,
      lineupIndex,
      date,
    ] as const,
  nextWeek: (playerId: string, draftId?: string, lineupIndex?: number) =>
    [...lineupKeys.all, "next-week", playerId, draftId, lineupIndex] as const,
  recruitmentPrice: (
    playerId: string,
    artistId: string,
    draftId?: string,
    lineupIndex?: number,
  ) =>
    [
      ...lineupKeys.all,
      "recruitment-price",
      playerId,
      artistId,
      draftId,
      lineupIndex,
    ] as const,
};

export function useCreateLineup() {
  const queryClient = useQueryClient();

  return useMutation<CreateLineupResponse, ApiError, CreateLineupBody>({
    mutationFn: createLineup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...playerKeys.all, "lineups"],
      });
    },
  });
}

export function useGetCurrentLineup(
  playerId: string,
  params: { draftId?: string; lineupIndex?: number },
) {
  return useQuery<CurrentLineup, ApiError>({
    queryKey: lineupKeys.current(playerId, params.draftId, params.lineupIndex),
    queryFn: () =>
      getCurrentLineup(
        playerId,
        params as { draftId: string; lineupIndex: number },
      ),
    enabled: !!params.draftId && !!params.lineupIndex,
  });
}

export function useGetNextWeekLineup(
  playerId: string,
  params: { draftId?: string; lineupIndex?: number },
) {
  return useQuery<NextWeekLineup, ApiError>({
    queryKey: lineupKeys.nextWeek(playerId, params.draftId, params.lineupIndex),
    queryFn: () =>
      getNextWeekLineup(
        playerId,
        params as { draftId: string; lineupIndex: number },
      ),
    enabled: !!params.draftId && !!params.lineupIndex,
  });
}

export function useArtistRecruitmentPrice(
  playerId: string,
  artistId: string | undefined,
  params: { draftId?: string; lineupIndex?: number },
) {
  return useQuery<RecruitmentPriceResponse, ApiError>({
    queryKey: lineupKeys.recruitmentPrice(
      playerId,
      artistId ?? "",
      params.draftId,
      params.lineupIndex,
    ),
    queryFn: () =>
      getRecruitmentPrice(
        playerId,
        artistId as string,
        params as { draftId: string; lineupIndex: number },
      ),
    enabled: !!artistId && !!params.draftId && !!params.lineupIndex,
  });
}

export function useArtistsRecruitmentPrices(
  playerId: string,
  artistIds: string[],
  params: { draftId?: string; lineupIndex?: number },
) {
  return useQueries({
    queries: artistIds.map((artistId) => ({
      queryKey: lineupKeys.recruitmentPrice(
        playerId,
        artistId,
        params.draftId,
        params.lineupIndex,
      ),
      queryFn: () =>
        getRecruitmentPrice(
          playerId,
          artistId,
          params as { draftId: string; lineupIndex: number },
        ),
      enabled: !!params.draftId && !!params.lineupIndex,
    })),
  });
}

export function useTradeLineup() {
  const queryClient = useQueryClient();

  return useMutation<TradeLineupResponse, ApiError, TradeLineupBody>({
    mutationFn: tradeLineup,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...lineupKeys.all, "current"],
      });
      queryClient.invalidateQueries({
        queryKey: [...lineupKeys.all, "next-week"],
      });
      queryClient.invalidateQueries({
        queryKey: [...playerKeys.all, "lineups"],
      });
    },
  });
}

export function useCurrentLineup(
  playerId: string,
  params: {
    draftId?: string;
    lineupIndex?: number;
  },
) {
  const lineupQuery = useGetCurrentLineup(playerId, params);

  const artistIds = useMemo(
    () => lineupQuery.data?.artists.map((a) => a.id) ?? [],
    [lineupQuery.data],
  );

  const priceQueries = useGetArtistPrices(artistIds, params.draftId);
  const pointsQueries = useGetArtistsPoints(artistIds, {});
  const recruitmentPriceQueries = useArtistsRecruitmentPrices(
    playerId,
    artistIds,
    params,
  );

  const isPending =
    lineupQuery.isPending ||
    priceQueries.some((q) => q.isPending) ||
    pointsQueries.some((q) => q.isPending) ||
    recruitmentPriceQueries.some((q) => q.isPending);

  const data = useMemo<EnrichedCurrentLineup | undefined>(() => {
    if (!lineupQuery.data) return undefined;
    if (
      priceQueries.some((q) => !q.data) ||
      pointsQueries.some((q) => !q.data) ||
      recruitmentPriceQueries.some((q) => !q.data)
    )
      return undefined;

    return {
      ...lineupQuery.data,
      artists: lineupQuery.data.artists.map((artist, i) => ({
        ...artist,
        artistPrice: priceQueries[i].data!,
        artistPoints: pointsQueries[i].data!,
        artistRecruitmentPrice:
          recruitmentPriceQueries[i].data!.recruitmentPrice,
      })),
    };
  }, [lineupQuery.data, priceQueries, pointsQueries, recruitmentPriceQueries]);

  return { data, isPending };
}

export function useNextWeekLineup(
  playerId: string,
  params: {
    draftId?: string;
    lineupIndex?: number;
    startDate?: string;
    endDate?: string;
  },
) {
  const lineupQuery = useGetNextWeekLineup(playerId, params);

  const artistIds = useMemo(
    () => lineupQuery.data?.artists.map((a) => a.id) ?? [],
    [lineupQuery.data],
  );

  const priceQueries = useGetArtistPrices(artistIds, params.draftId);
  const pointsQueries = useGetArtistsPoints(artistIds, {
    startDate: params.startDate,
    endDate: params.endDate,
  });

  const isPending =
    lineupQuery.isPending ||
    priceQueries.some((q) => q.isPending) ||
    pointsQueries.some((q) => q.isPending);

  const data = useMemo<EnrichedNextWeekLineup | undefined>(() => {
    if (!lineupQuery.data) return undefined;
    if (priceQueries.some((q) => !q.data) || pointsQueries.some((q) => !q.data))
      return undefined;

    return {
      ...lineupQuery.data,
      artists: lineupQuery.data.artists.map((artist, i) => ({
        ...artist,
        artistPrice: priceQueries[i].data!,
        artistPoints: pointsQueries[i].data!,
      })),
    };
  }, [lineupQuery.data, priceQueries, pointsQueries]);

  return { data, isPending };
}

export const CurrentLineupIndexContext = createContext<{
  currentLineupIndex: number | undefined;
  setCurrentLineupIndex: (index: number | undefined) => void;
}>({
  currentLineupIndex: undefined,
  setCurrentLineupIndex: () => {},
});

export const useCurrentLineupIndex = () =>
  useContext(CurrentLineupIndexContext);
